import { Button, Col, Row, Table, Modal } from "@themesberg/react-bootstrap";
import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion/dist/framer-motion";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import ActionMenu from "../components/ActionMenu";
import useBankingDetails from "../hooks/useBankingDetails";
import Preloader from "../../../../components/Preloader";
import AddEditModal from "./AddEditModal";
import PaginationComponent from "../../../../components/Pagination";
import NotificationIconContainer from "../../../../components/NotificationIcon/NotificationIconContainer";
import { capitalize } from "@mui/material";
import { getBankingDetailsHeaders } from "../helpers";
import Toggle from "../../../../components/Toggle/Toggle";
import useTableCustom from "../../../../components/CusotmFilter"

import {
  AddButtonStyled,
  ButtonContainer,
  HeaderSection,
  ResponsiveTitle,
  TableContainer,
} from "../style";
import useCheckPermission from "../../../../utils/checkPermission";
import TableErrorMessage from "../../../../components/TableErrorMessage";

export default function AddBankDetails({ bankType }) {
  const {
    page,
    limit,
    setPage,
    setLimit,
    loading,
    handleDelete,
    handleCreateEdit,
    handleToggleStatus,
    type,
    data,
    setShow,
    show,
    createUpdate,
    dispatch,
    bankingDetails,
    validation,
    setValidation,
    totalPages,
    totalCount
  } = useBankingDetails(bankType);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const { isHidden } = useCheckPermission();

  const handleToggleChange = (bankDetailsId, newStatus, bankName, rtNumber) => {
    setPendingStatusChange({
      bankDetailsId,
      newStatus,
      rtNumber,
    });
    setShowConfirmModal(true);
  };



  const confirmStatusChange = () => {
    if (pendingStatusChange) {
      handleToggleStatus(
        pendingStatusChange.bankDetailsId,
        pendingStatusChange.newStatus
      );
    }
    setShowConfirmModal(false);
    setPendingStatusChange(null);
  };

  const cancelStatusChange = () => {
    setShowConfirmModal(false);
    setPendingStatusChange(null);
  };

  // Step 1: Get all headers
  let bankingDetailsHeaders = getBankingDetailsHeaders(bankType);
  const hastoglePermission = !isHidden({ module: { key: "TransactionsBanking", value: "T" } });
  // Step 2: Apply permission checks (remove columns if user has no access)
  const hasUpdatePermission = !isHidden({ module: { key: "TransactionsBanking", value: "U" } });
  const hasDeletePermission = !isHidden({ module: { key: "TransactionsBanking", value: "D" } });

  if (!hastoglePermission) {
    bankingDetailsHeaders = bankingDetailsHeaders.filter((h) => h.value !== "status");
  }
  if (!hasUpdatePermission && !hasDeletePermission) {
    bankingDetailsHeaders = bankingDetailsHeaders.filter((h) => h.value !== "action");
  }
  // ✅ Step 3: Pass filtered headers into useTableCustom
  const {
    visibleColumns,
    pageData,
    customColSetting,
    noDataFound,
  } = useTableCustom(
    bankingDetails,
    bankingDetailsHeaders, // already permission-filtered
    false,
    () => { },
    null,
    null,
    false,
    "",
    true,
    bankType === "deposit" ? "ADMIN_BANK_DETAILS_DEPOSIT" : "ADMIN_BANK_DETAILS_WITHDRAWAL"
  );

  // ✅ Step 4: Filter columns to show in table
  const visibleKeys = visibleColumns.map((col) => col.value);
  const filteredHeaders = bankingDetailsHeaders.filter((col) =>
    visibleKeys.includes(col.value)
  );





  const headerRef = useRef(null);
  const filterRef = useRef(null);
  const paginationRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState();

  useEffect(() => {
    const updateMaxHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const filterHeight = filterRef.current?.offsetHeight || 0;
      const paginationHeight = paginationRef?.current?.offsetHeight || 0;

      const responsiveTable = document.querySelector('.table-responsive');
      const totalHeight = headerHeight + filterHeight + paginationHeight;
      const availableHeight = window.innerHeight - totalHeight - 110;

      if (window.innerWidth < 1000) {
        // For smaller screens: reset to full height
        if (responsiveTable) {
          responsiveTable.style.maxHeight = '100%';
          responsiveTable.style.overflowY = 'unset';
        }
        setTableMaxHeight('100%');
        return;
      }
      else if (responsiveTable) {
        responsiveTable.style.maxHeight = `${availableHeight}px`;
        responsiveTable.style.overflowY = 'auto'; // ensure scrolling
      }

      setTableMaxHeight(`${availableHeight}px`);
    };

    updateMaxHeight();
  }, []);

  let tBody = null;



  if (bankType === "deposit") {
    tBody = bankingDetails?.map((item, index) => (
      <tr key={item.bank_details_id || index}>
        {filteredHeaders.map((col) => {
          const { value } = col;
          switch (value) {
            case "bankName":
              return <td key="bankName">{item.bankName || "NA"}</td>;
            case "rtNumber":
              return <td key="rtNumber">{item.rtNumber || "NA"}</td>;
            case "accountHolderName":
              return (
                <td key="accountHolderName">{item.accountHolderName || "NA"}</td>
              );
            case "accountNumber":
              return <td key="accountNumber">{item.accountNumber || "NA"}</td>;

            case "bank_details_id":
              return <td key="bank_details_id">{item.bank_details_id || "NA"}</td>;

            case "accountType":
              return <td key="accountType">{item.accountType || "NA"}</td>;
            case "place":
              return <td key="place">{item.place || "NA"}</td>;
            case "status":
              return (
                <td key="status">
                  <Toggle
                    checked={item.status}
                    onChange={(e) =>
                      handleToggleChange(
                        item.bank_details_id,
                        e.target.checked,
                        item.bankName,
                        item.rtNumber
                      )
                    }
                    align="left"
                    size="small"
                  />
                </td>
              );
            case "action":
              return (
                <td key="action">
                  <ActionMenu
                    handleCreateEdit={handleCreateEdit}
                    handleDelete={handleDelete}
                    data={{
                      bankDetailsId: item.bank_details_id,
                      bankName: item.bankName,
                      accountHolderName: item.accountHolderName,
                      accountNumber: item.accountNumber,
                      accountType: item.accountType,
                      status: item.status,
                      place: item.place,
                      rtNumber: item.rtNumber,
                    }}
                  />
                </td>
              );
            default:
              return null;
          }
        })}
      </tr>
    ));
  }
  else {
    tBody = bankingDetails?.map((item, index) => (
      <tr key={item.bank_details_id || index}>
        {filteredHeaders.map((col) => {
          const { value } = col;
          switch (value) {
            case "bankName":
              return <td key="bankName">{item.bankName || "NA"}</td>;
            case "rtNumber":
              return <td key="rtNumber">{item.rtNumber || "NA"}</td>;

            case "bank_details_id":
              return <td key="bank_details_id">{item.bank_details_id || "NA"}</td>;

            case "status":
              return (
                <td key="status">
                  <Toggle
                    checked={item.status}
                    onChange={(e) =>
                      handleToggleChange(
                        item.bank_details_id,
                        e.target.checked,
                        item.bankName,
                        item.rtNumber
                      )
                    }
                    align="left"
                    size="small"
                  />
                </td>
              );
            case "action":
              return (
                <td key="action">
                  <ActionMenu
                    handleCreateEdit={handleCreateEdit}
                    handleDelete={handleDelete}
                    data={{
                      bankDetailsId: item.bank_details_id,
                      bankName: item.bankName,
                      rtNumber: item.rtNumber,
                      status: item.status,
                    }}
                  />
                </td>
              );
            default:
              return null;
          }
        })}
      </tr>
    ));
  }


  return (
    <>
      <HeaderSection className="section-header-wrap" ref={headerRef}>
        <Row className="justify-content-between align-items-center g-3">
          <Col xs={12} md={6}>
            <ResponsiveTitle className="title-text">
              Admin Bank Details ({capitalize(bankType)})
            </ResponsiveTitle>
          </Col>

          <Col xs={12} md={6}>
            <ButtonContainer className="d-flex justify-content-md-end">
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="flex-grow-1 flex-md-grow-0"
                hidden={isHidden({
                  module: { key: "TransactionsBanking", value: "C" },
                })}
              >
                <AddButtonStyled
                  variant="primary"
                  onClick={() => handleCreateEdit("Create")}
                >
                  <AddCircleOutlineRoundedIcon />
                  <span>Add Bank Details</span>
                </AddButtonStyled>
              </motion.div>
              <div className="ms-auto ms-md-2">
                <NotificationIconContainer />
              </div>
            </ButtonContainer>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col className="d-flex justify-content-end">
            <div style={{ maxWidth: "250px", width: "100%" }}>
              {customColSetting}
            </div>
          </Col>
        </Row>

      </HeaderSection>

      {loading ? (
        <Preloader />
      ) : (
        visibleColumns?.length > 0 ? <>
          <TableContainer className="TableBg">
            <div className="CustomTableWrapper">
              <div className="table-scroll-wrapper" style={{ maxHeight: tableMaxHeight }}>
                <Table striped responsive hover size="sm" className="text-center">
                  <thead className="thead-light">
                    <tr>
                      {visibleColumns.map((h) => (
                        <th key={h.value}>{h.label}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {tBody}
                    {(!bankingDetails || bankingDetails?.length === 0) && (
                      <tr>
                        <td
                          colSpan={bankingDetailsHeaders.length}
                          className="text-danger text-center"
                        >
                          No data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

            </div>
          </TableContainer>

          {bankingDetails?.count !== 0 && !loading && (
            <PaginationComponent
              page={bankingDetails?.count < page ? setPage(1) : page}
              totalPages={totalPages}
              setPage={setPage}
              limit={limit}
              setLimit={setLimit}
              showTotalCount={true}
              totalCount={totalCount}
              countText="Total Admin Bank Details : "
            />
          )}
        </> : <TableErrorMessage />

      )}



      <AddEditModal
        type={type}
        data={data}
        show={show}
        setShow={setShow}
        createUpdate={createUpdate}
        dispatch={dispatch}
        bankingDetails={bankingDetails}
        validation={validation}
        setValidation={setValidation}
        bankType={bankType}
      />

      <Modal
        show={showConfirmModal}
        onHide={cancelStatusChange}
        centered
        backdrop="static"
        style={{
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(15px)",
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Status Change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingStatusChange && (
            <div>
              <p>
                Are you sure you want to{" "}
                <strong>
                  {pendingStatusChange.newStatus ? "activate" : "deactivate"}
                </strong>{" "}
                the bank details for RT No.
                <strong>"{pendingStatusChange.rtNumber}"</strong>?
              </p>
              <div className="d-flex align-items-center mt-3">
                <strong>Status will change to: </strong>
                <span
                  className={`ms-2 badge ${pendingStatusChange.newStatus ? "bg-success" : "bg-danger"
                    }`}
                >
                  {pendingStatusChange.newStatus ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={pendingStatusChange?.newStatus ? "success" : "danger"}
            onClick={confirmStatusChange}
          >
            Confirm{" "}
            {pendingStatusChange?.newStatus ? "Activation" : "Deactivation"}
          </Button>
          <Button variant="secondary" onClick={cancelStatusChange}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
